import { useCallback, memo, useEffect } from 'react'
import { useSelector } from 'react-redux'

import * as Styled from './styled'
import { EVENT_TYPES, eventBus } from 'services/event-bus'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { editorSelectors } from 'store/features/editor/selectors'

import {
  usePlayableAreaWidth,
  useScrollWhenPlaying,
  useCorrectPlayheadTimeOnDurationChange
} from 'hooks/timeline'
import { useHorizontalDragDrop } from 'hooks/useHorizontalDragDrop'
import { useTimelineContext } from 'context/TimelineContext'
import PlayHint from './PlayHint'

import PlayHeadIcon from 'assets/svgr-icons/PlayHead.svg'
import { useWebWoodContext } from 'context/WebWoodContext'
import { ProcessingEvent } from '@renderforest/webwood-engine'

const PlayHead = ({ height }: { height: number }) => {
  const projectDuration = useSelector(projectDataSelectors.duration)
  const isAnyBuffering = useSelector(editorSelectors.isAnyBuffering)
  const playableAreaWidth = usePlayableAreaWidth()

  useCorrectPlayheadTimeOnDurationChange()

  const {
    isPlaying,
    playHeadRef,
    playHeadClickableAreaRef,
    scrollableContainerRef,
    onPause,
    resumePlaying,
    setPlayHeadOffsetLeft,
    setPlayHeadCurrentTime,
    getCurrentTimeByOffsetLeft,
    getCurrentTimeImmediate
  } = useTimelineContext()

  const { engine } = useWebWoodContext()

  /**
   * This is made to have more smooth jump
   * to the start of the timeline and proper sync
   * with webwood engine
   */
  const handleAnimationEnd = useCallback(() => {
    setPlayHeadCurrentTime(0)
    eventBus.publish(EVENT_TYPES.PLAYBACK_TIME_CHANGE, 0)
    onPause()
  }, [onPause, setPlayHeadCurrentTime])

  useEffect(() => {
    if (!engine) return
    engine.signals.on(ProcessingEvent.PLAYBACK_ENDED, handleAnimationEnd)
    return () => {
      engine.signals.off(ProcessingEvent.PLAYBACK_ENDED, handleAnimationEnd)
    }
  }, [engine, handleAnimationEnd])

  const pausePlayHeadAnimation = useCallback(() => {
    if (!playHeadRef.current) return
    playHeadRef.current.style.animationPlayState = 'paused'
  }, [playHeadRef])

  const resumePlayHeadAnimation = useCallback(() => {
    if (!playHeadRef.current) return
    playHeadRef.current.style.animationPlayState = 'running'
  }, [playHeadRef])

  useEffect(() => {
    if (isAnyBuffering) {
      pausePlayHeadAnimation()
    } else {
      resumePlayHeadAnimation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyBuffering])

  useEffect(() => {
    const currentTime = getCurrentTimeImmediate()
    if (currentTime >= projectDuration) {
      onPause()
      setPlayHeadCurrentTime(projectDuration)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDuration])

  const handleDrag = useCallback(
    (leftInPixels: number) => {
      setPlayHeadOffsetLeft(leftInPixels)
      const currentTime = getCurrentTimeByOffsetLeft(leftInPixels)

      eventBus.publish(EVENT_TYPES.PLAYBACK_TIME_CHANGE, currentTime)
    },
    [getCurrentTimeByOffsetLeft, setPlayHeadOffsetLeft]
  )

  useHorizontalDragDrop({
    elementRef: playHeadRef,
    containerRef: playHeadClickableAreaRef,
    scrollableContainerRef,
    handleDrop: setPlayHeadOffsetLeft,
    handleDragStart: onPause,
    handleDragEnd: resumePlaying,
    handleDrag
  })

  useScrollWhenPlaying({
    playHeadRef,
    scrollableContainerRef,
    isPlaying
  })

  return (
    <Styled.PlayHeadWrapper $height={height}>
      <Styled.PlayHeadArea $width={playableAreaWidth}>
        <Styled.PlayHeadClickableArea ref={playHeadClickableAreaRef} />
        <Styled.PlayHead ref={playHeadRef}>
          <Styled.PlayHeadHead>
            <PlayHeadIcon />
          </Styled.PlayHeadHead>
          <Styled.PlayHeadLine />
        </Styled.PlayHead>
        <PlayHint />
      </Styled.PlayHeadArea>
    </Styled.PlayHeadWrapper>
  )
}

export default memo(PlayHead)
